import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider.js';
import { listEvidence, openEvidence } from '../api/quality.js';
import { ApiError } from '../api/client.js';

const KINDS = ['PHOTO', 'VIDEO', 'GPS_PIN', 'DOCUMENT', 'REWORK_PROOF'] as const;

export function EvidenceRequestPage(): ReactNode {
  const { id = '' } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly unknown[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [instructions, setInstructions] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await listEvidence(id, token)); } catch (err) { setError(err instanceof ApiError ? err.message : 'Load failed'); }
  }, [id, token]);
  useEffect(() => { void load(); }, [load]);

  function toggle(k: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }

  async function submit(): Promise<void> {
    setError(null); setMsg(null); setBusy(true);
    try {
      await openEvidence(id, { requestedKinds: Array.from(selected), instructions }, token);
      setMsg('Evidence requested');
      setSelected(new Set()); setInstructions('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed');
    } finally { setBusy(false); }
  }

  return (
    <main aria-label="evidence-request">
      <h1>Evidence — {id}</h1>
      {error ? <div role="alert">{error}</div> : null}
      {msg ? <div role="status">{msg}</div> : null}
      <div aria-label="evidence-count">{rows.length} request(s)</div>
      <form aria-label="evidence-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <fieldset aria-label="kinds">
          <legend>Requested kinds</legend>
          {KINDS.map((k) => (
            <label key={k}>
              <input type="checkbox" aria-label={`kind-${k}`} checked={selected.has(k)} onChange={() => toggle(k)} />
              {k}
            </label>
          ))}
        </fieldset>
        <label>Instructions
          <textarea aria-label="evidence-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value)} required />
        </label>
        <button type="submit" disabled={busy || selected.size === 0 || !instructions}>Request evidence</button>
      </form>
    </main>
  );
}
